import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import ElevatedButton from '../Layout/ElevatedButton';
import TwoFactorSetup from './TwoFactorSetup';
import RegisterAccountFooter from 'components/Layout/RegisterAccountFooter';

const Form = styled.form`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 8px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${props => props.theme.text};
`;

const AuthPage = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 1rem;
  gap: 1rem;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
`;

const AuthContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
`;

const BackLink = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.5rem 0;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &:hover {
    color: ${props => props.theme.text};
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid ${props => props.theme.buttonPrimary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const Checkbox = styled.input`
  margin-right: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  margin: 1rem 0;
  color: #b9bbbe;
  cursor: pointer;

  &:hover {
    color: white;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 2px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textSecondary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.inputBorderFocus};
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 8px rgba(88, 101, 242, 0.3);
  }
`;

const RegisterForm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { prefetchData } = useData();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitter = e.nativeEvent?.submitter;
    if (submitter && submitter.dataset?.registerSubmit !== 'true') {
      return;
    }

    setError('');

    // Prevent registration with admin email
    if (email.toLowerCase() === 'admin@planmysemester.com') {
      setError('This email address is reserved for administrative use');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must contain uppercase, lowercase, and number');
      return;
    }

    setLoading(true);

    const result = await register(name, email, password);
    
    if (result.success) {
      if (result.twoFactor) {
        // Show 2FA setup
        setShow2FASetup(true);
      } else {
        // Prefetch all data before navigating to dashboard
        await prefetchData();
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handle2FAComplete = async () => {
    // Prefetch all data before navigating to dashboard
    await prefetchData();
    navigate('/dashboard');
  };

  const handle2FACancel = () => {
    setShow2FASetup(false);
    // Could navigate to dashboard or stay on register form
    navigate('/dashboard');
  };

  if (show2FASetup) {
    return <TwoFactorSetup onComplete={handle2FAComplete} onCancel={handle2FACancel} />;
  }

  return (
    <AuthPage>
      <AuthContent>
        <BackLink onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          navigate('/');
        }}>
          ← Back to Home
        </BackLink>

        <Form onSubmit={handleSubmit}>
        <h2 style={{ color: 'inherit', textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>Register</h2>
        
        {error && (
          <div style={{ 
            padding: '0.75rem', 
            marginBottom: '1rem', 
            background: '#dc3545', 
            color: 'white', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
        
        <Input
          type="text"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={loading}
        />
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
        />
        <Input
          type="password"
          placeholder="Password (min 8 chars, uppercase, lowercase, number)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
        />
        <Input
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={loading}
        />
        
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            required
            disabled={loading}
          />
          Accept Terms of Service
        </CheckboxLabel>

        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            checked={acceptPrivacyPolicy}
            onChange={(e) => setAcceptPrivacyPolicy(e.target.checked)}
            required
            disabled={loading}
          />
          Accept Privacy Policy
        </CheckboxLabel>
        
        <ElevatedButton
          type="submit"
          data-register-submit="true"
          style={{ width: '100%', marginTop: '1rem' }}
          disabled={loading}
        >
          {loading ? 'Creating account...' : 'Register'}
        </ElevatedButton>
        </Form>
      </AuthContent>

      <RegisterAccountFooter />
    </AuthPage>
  );
};

export default RegisterForm;